import { motion } from 'motion/react';
import { Plus } from 'lucide-react';
import { cn, formatCurrency } from '../lib/utils';

interface BudgetTabProps {
  categoryData: { name: string; budget: number; actual: number }[];
  localCurrency: string;
  onManageBudgets: () => void;
  isPrivacyMode: boolean;
}

export function BudgetTab({ categoryData, localCurrency, onManageBudgets, isPrivacyMode }: BudgetTabProps) {
  const sortedCategoryData = [...categoryData].sort((a, b) => b.actual - a.actual);
  const totalBudget = categoryData.reduce((acc, curr) => acc + curr.budget, 0);
  const totalActual = categoryData.reduce((acc, curr) => acc + curr.actual, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Budget</h2>
        <button onClick={onManageBudgets} className="flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" />
          Manage
        </button>
      </div>

      <div className="bg-zinc-900 dark:bg-zinc-800 text-white p-8 rounded-[32px] shadow-2xl shadow-zinc-200 dark:shadow-zinc-900/50 transition-colors">
        <div className="text-center space-y-2">
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Total Budget Usage</p>
          <h2 className={cn("text-4xl font-bold tracking-tight", totalActual > totalBudget ? "text-rose-400" : "text-emerald-400", isPrivacyMode && "blur-md select-none")}>
            {formatCurrency(totalActual, localCurrency)}
          </h2>
          <p className={cn("text-sm text-zinc-400", isPrivacyMode && "blur-sm select-none")}>of {formatCurrency(totalBudget, localCurrency)} limit</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedCategoryData.map((cat) => {
          const percentage = cat.budget > 0 ? (cat.actual / cat.budget) * 100 : 0;
          const isOverBudget = percentage > 100;
          const isWarning = percentage > 85 && !isOverBudget;
          const remaining = cat.budget - cat.actual;
          
          return (
            <div key={cat.name} className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-zinc-100 dark:border-zinc-800 shadow-sm flex flex-col gap-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider truncate max-w-[120px]" title={cat.name}>{cat.name}</p>
                  <p className={cn("text-lg font-bold text-zinc-900 dark:text-zinc-100 mt-1", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(cat.actual, localCurrency)}</p>
                </div>
                <div className={cn("px-2 py-1 rounded-lg text-[10px] font-bold h-fit", isOverBudget ? "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400" : isWarning ? "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400")}>
                  {cat.budget > 0 ? `${percentage.toFixed(0)}%` : 'No Limit'}
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-[10px] font-medium">
                  <span className={cn("text-zinc-400", isPrivacyMode && "blur-sm select-none")}>Target: {formatCurrency(cat.budget, localCurrency)}</span>
                  <span className={cn(isOverBudget ? "text-rose-500" : "text-emerald-500", isPrivacyMode && "blur-sm select-none")}>{isOverBudget ? `Over: ${formatCurrency(Math.abs(remaining), localCurrency)}` : `Left: ${formatCurrency(remaining, localCurrency)}`}</span>
                </div>
                <div className="h-2 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(percentage, 100)}%` }} transition={{ duration: 1, ease: "easeOut" }} className={cn("h-full rounded-full", isOverBudget ? "bg-rose-500" : isWarning ? "bg-amber-500" : "bg-indigo-500")} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}